import React from 'react';
import { sanitizeHtml } from '@/lib/sanitize';
import { cn } from '@/lib/utils';
import { editableProps, useIsVisualEditor } from './directus-ve';
import type { InlineRichTextProps } from './types';

export type { InlineRichTextProps } from './types';

/**
 * InlineRichText renders rich-text content and marks it as editable for the
 * Directus Visual Editor. Clicking an element inside the Directus Studio
 * overlay opens the Directus drawer; the actual WYSIWYG editing lives in the
 * Studio, so this component only renders content plus the `data-directus`
 * attribute when editing is enabled.
 */
export const InlineRichText: React.FC<InlineRichTextProps> = ({
  collection,
  itemId,
  field,
  value,
  canEdit = true,
  className = '',
  displayClassName = '',
  placeholder = '',
  children,
}) => {
  // Editing is allowed ONLY inside the Directus Studio Visual Editor. When the
  // page is browsed normally (even by an authenticated admin) nothing is marked
  // editable, so the content renders as plain, read-only HTML.
  const isVisualEditor = useIsVisualEditor();
  const enabled = canEdit && isVisualEditor;

  const html = value ?? '';

  return (
    <div
      className={cn('inline-rich-text', className, displayClassName)}
      {...editableProps(enabled, { collection, item: itemId, fields: field })}
    >
      {children ??
        (html ? (
          <div
            className="prose prose-sm max-w-none"
            dangerouslySetInnerHTML={{ __html: sanitizeHtml(html) }}
          />
        ) : (
          placeholder && <span className="text-gray-400 italic">{placeholder}</span>
        ))}
    </div>
  );
};

export default InlineRichText;
